// components/ProfAvatar.tsx — Il Prof's tutor mark (redesign spec §4.2).
//
// P9 (redesign rebuild): replaces the old open-book line glyph (which was ported
// from design-2026-04-21). The CHOSEN symbol per spec §4.2 is a **horizon arc +
// a saffron spark** above it, on the **brand-gradient squircle**. Same mark
// across light/dark and every size. Lives in chat threads, lesson chapters and
// the voice orb — never in nav (the app logo lives there — §4.1 / AppLogo.tsx).

import type { JSX } from 'react';
import { useId } from 'react';
import { useTokens } from '../state/theme';

// Bright saffron spark — used on the saturated gradient squircle in BOTH modes
// (the dull light-mode SPARK #d4a04f is for small highlights on pale bg, not for
// sitting on the gradient). Matches the mockup (V4) exactly.
const SPARK_ON_GRADIENT = '#ffd76e';

export interface ProfMarkProps {
  size?: number;
}

/**
 * The Il Prof symbol on its brand-gradient squircle. `ProfMark` and `ProfAvatar`
 * both render this same mark so the tutor reads identically everywhere. Each
 * instance gets a unique gradient id (multiple avatars render in one thread).
 */
export function ProfMark({ size = 22 }: ProfMarkProps): JSX.Element {
  const tokens = useTokens();
  // ':' from useId() is invalid inside url(#..) references — strip it.
  const gradId = `profGrad-${useId().replace(/:/g, '')}`;
  const [g0, g1] = tokens.mode === 'dark'
    ? ['#7c5aff', '#ff5ad8']   // spec §3.3 dark gradient (more saturated)
    : ['#6943ff', '#e547c4'];  // spec §3.3 light gradient
  // Decorative: the "Il Prof" text label always accompanies the mark, so the svg
  // is aria-hidden — avoids a duplicate image in the a11y tree and keeps it out
  // of role="img" content-image queries on a screen.
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" style={{ flexShrink: 0 }} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={g0}/>
          <stop offset="100%" stopColor={g1}/>
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="48" height="48" rx="14" fill={`url(#${gradId})`}/>
      {/* horizon arc */}
      <path d="M10 32 Q24 16 38 32" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" fill="none"/>
      {/* saffron spark above the horizon */}
      <circle cx="24" cy="14.5" r="2.6" fill={SPARK_ON_GRADIENT}/>
    </svg>
  );
}

export interface ProfAvatarProps {
  size?: number;
}

/** Chat-size Il Prof avatar (default 28). Same mark as ProfMark. */
export function ProfAvatar({ size = 28 }: ProfAvatarProps): JSX.Element {
  return <ProfMark size={size}/>;
}
